import { getApp, initializeApp } from 'firebase/app';
import { Capacitor } from '@capacitor/core';
import { FirebaseAnalytics } from '@capacitor-firebase/analytics';
import { FacebookLogin } from '@capacitor-community/facebook-login';
import { getAuth, indexedDBLocalPersistence, initializeAuth, setPersistence } from 'firebase/auth';
import { AuthStateChange, FirebaseAuthentication, Persistence } from '@capacitor-firebase/authentication';

// Services
import { recordEvent, updateIdentity } from './user.service';
import { AUTH_ERROR_CODES } from '../providers/auth.provider';
import { connectFirestoreEmulator, getFirestore } from 'firebase/firestore';
import { getViewportType } from '../hooks/useWindowSize';
import { getUtmParams } from '../analytics/segment';

// Environment variables
import {
  FIREBASE_API_KEY,
  FIREBASE_AUTH_DOMAIN,
  FIREBASE_PROJECT_ID,
  FIREBASE_MESSAGING_SENDER_ID,
  FIREBASE_APP_ID,
  FIREBASE_MEASUREMENT_ID,
  USE_EMULATOR,
  FIREBASE_AUTH_EMULATOR_HOST,
  FIREBASE_AUTH_EMULATOR_PORT,
  FIREBASE_STORE_EMULATOR_HOST,
  FIREBASE_STORE_EMULATOR_PORT,
} from '../env';

const app = initializeApp({
  apiKey: FIREBASE_API_KEY || 'AIzaSyB6FVqG03uPrGR3nD79Rp22kxCCCiqm3ys', // This fallback apiKey is OK to be in plain text
  authDomain: FIREBASE_AUTH_DOMAIN || 'solve-mdop-staging.firebaseapp.com',
  projectId: FIREBASE_PROJECT_ID || 'solve-mdop-staging',
  messagingSenderId: FIREBASE_MESSAGING_SENDER_ID || '129832983205',
  appId: FIREBASE_APP_ID || '1:129832983205:web:2e61ccca7d32db5c55cb4c',
  measurementId: FIREBASE_MEASUREMENT_ID,
});

const UTM_PROP_MAP = {
  utmSource: 'source',
  utmMedium: 'medium',
  utmCampaign: 'campaign',
  gclid: 'gclid',
};

getUtmParams().then((params) => {
  for (let i = 0; i < Object.keys(params).length; i++) {
    const key = Object.keys(params)[i];

    if (params[key] !== null && UTM_PROP_MAP[key]) {
      setFirebaseAnalyticsUserProperty(UTM_PROP_MAP[key], params[key]);
    }
  }
});

let currAuth;

export const getFirebaseAuth = () => {
  if (currAuth) return currAuth;

  if (Capacitor.isNativePlatform()) {
    currAuth = initializeAuth(getApp(), {
      persistence: indexedDBLocalPersistence,
    });
  } else {
    currAuth = getAuth();

    setPersistence(currAuth, indexedDBLocalPersistence);
  }

  return currAuth;
};

const startEmulators = async () => {
  // Native plugins (e.g. @capacitor-firebase/authentication) require a bare
  // host with no scheme; the web SDK tolerates either, so strip it here so
  // both platforms get a valid host.
  const authHost = FIREBASE_AUTH_EMULATOR_HOST.replace(/^https?:\/\//, '');
  const storeHost = FIREBASE_STORE_EMULATOR_HOST.replace(/^https?:\/\//, '');

  await FirebaseAuthentication.useEmulator({
    host: authHost,
    port: FIREBASE_AUTH_EMULATOR_PORT,
  });

  connectFirestoreEmulator(getFirestore(), storeHost, FIREBASE_STORE_EMULATOR_PORT);
};

export const startListeners = async ({
  setFirebaseUser,
  setLoading,
  setError,
  processEventQueue,
  setInitialized,
  setInvisibleCaptcha,
  destroyRecaptcha,
  setRequestOTP,
}) => {
  await FirebaseAuthentication.removeAllListeners();

  try {
    await FirebaseAuthentication.addListener('authStateChange', (change: AuthStateChange) => {
      setFirebaseUser?.(change.user ?? null);

      let platform = 'unknown';
      try {
        platform = Capacitor.getPlatform();
      } catch {
        console.warn('Capacitor is not supported');
      }

      if (change?.user) {
        setFirebaseAnalyticsUserId(change.user.uid);
        processEventQueue(change.user);
        updateIdentity({
          platform,
          viewport: getViewportType(),
        });
      }
    });

    await FirebaseAuthentication.addListener('phoneVerificationCompleted', (phoneVerification) => {
      console.debug('Phone Auth: Verification Completed Event');
      setLoading(false);
      setFirebaseUser(phoneVerification.user);

      recordEvent({
        eventName: 'Sign Up',
        firebaseUser: phoneVerification.user,
        props: { type: 'PHONE' },
      });
    });

    await FirebaseAuthentication.addListener('phoneCodeSent', (event) => {
      console.debug('Phone Auth: Verification ID Event Received', event);
      window.verificationId = event.verificationId;
      setLoading(false);
    });

    await FirebaseAuthentication.addListener('phoneVerificationFailed', (err) => {
      console.debug('Phone Auth: Verification Failed Event — switching to visible', err);
      setInvisibleCaptcha(false);
      destroyRecaptcha();
      setLoading(false);
      setRequestOTP(false);
      setError(AUTH_ERROR_CODES.PHONE_VERIFICATION_FAILED);
    });
  } catch (e) {
    console.error('Error starting Firebase listeners', e);
  }
};

export const initializeFirebase = async ({
  setFirebaseUser,
  setLoading,
  setError,
  processEventQueue,
  setInitialized,
  setInvisibleCaptcha,
  destroyRecaptcha,
  setRequestOTP,
}) => {
  if (USE_EMULATOR) {
    await startEmulators();
  }

  const auth = getFirebaseAuth();

  try {
    await FirebaseAuthentication.setPersistence({
      persistence: Persistence.IndexedDbLocal,
    });
  } catch (e) {
    // Safari Private mode or restricted storage may fail; fall back silently
    console.warn('Auth persistence could not be set; continuing.', e);
  }

  await startListeners({
    setFirebaseUser,
    setLoading,
    setError,
    processEventQueue,
    setInitialized,
    setInvisibleCaptcha,
    destroyRecaptcha,
    setRequestOTP,
  });

  const currentUser = await FirebaseAuthentication.getCurrentUser();
  setFirebaseUser?.(currentUser.user ?? null);
  setInitialized(true);
  setLoading(false);
};

export const initializeFacebook = async () => {
  await FacebookLogin.initialize({ appId: '1284865695490835' });
  await FacebookLogin.setAutoLogAppEventsEnabled({ enabled: true });

  // NOTE: Enable these if Facebook Ads isn't getting the tracking data we need
  // await FacebookLogin.setAdvertiserTrackingEnabled({ enabled: true });
  // await FacebookLogin.setAdvertiserIDCollectionEnabled({ enabled: true });
};

export const setFirebaseAnalyticsUserId = async (userId) => {
  await FirebaseAnalytics.setUserId({
    userId,
  });
};

export const setFirebaseAnalyticsUserProperty = async (key, value) => {
  await FirebaseAnalytics.setUserProperty({
    key,
    value,
  });
};

export const trackFirebaseEvent = async (name, params) => {
  await FirebaseAnalytics.logEvent({
    name,
    params,
  });
};
